import {
    FC,
    MouseEvent,
    SetStateAction,
    useCallback,
    useMemo,
    useState,
} from "react";
import { WebcastEntry } from "@mediaspace/shared/types";
import merge from "lodash/merge";
import { useConfig } from "@mediaspace/shared/data-kms-config";
import { useCurrentTimeForAnimation } from "@mediaspace/hooks";
import HeroUnit from "./HeroUnit";
import { StaticImage } from "@mediaspace/shared/types";
import { isEmpty } from "ramda";

export interface HeroUnitContainerProps {
    enabled: boolean;
    isLive: boolean;
    entries?: WebcastEntry[];
    previewPlayerConfig: Record<string, unknown>;
    carouselInterval: number;
    className?: string;
    title?: string;
    // Static images gets priority over entries. Decision about which type to display (images or entries)
    // should be done on server side.
    staticImages?: StaticImage[];
    autoPlay?: boolean;
}

interface CarouselState {
    index: number;
    time: number;
}

enum MediaType {
    StaticImages = "Images",
    Entries = "Entries",
}

/*
 * Hero unit component of the home page.
 * Displays a carousel of live/VOD entries' video previews OR static imaged according to admin configuration.
 */
const HeroUnitContainer: FC<HeroUnitContainerProps> = (
    props: HeroUnitContainerProps
) => {
    const {
        enabled,
        isLive,
        className,
        entries = [],
        previewPlayerConfig,
        carouselInterval,
        title,
        staticImages = [],
        autoPlay = true,
    } = props;

    const mediaType = !isEmpty(staticImages)
        ? MediaType.StaticImages
        : MediaType.Entries;
    const mediaCount =
        mediaType === MediaType.StaticImages
            ? staticImages.length
            : entries.length;

    const [isActive, setIsActive] = useState(autoPlay);

    // The state of the carousel: the ID of the current entry and the time of showing it.
    const [carouselState, setCarouselState] = useState<CarouselState>(() => ({
        index: 0,
        time: 0,
    }));
    const { index: currentItemIndex, time: currentTime } = carouselState;

    // The carousel interval for specific entry could be less than usual because of small entry duration.
    const entry =
        mediaType === MediaType.Entries ? entries[currentItemIndex] : null;
    const { id, duration, thumbnailUrl, presenters } = entry || {};
    const name = entry ? entry.name : staticImages[currentItemIndex].title;
    const description = entry
        ? entry.description
        : staticImages[currentItemIndex].description;
    const entryCarouselInterval =
        mediaType === MediaType.Entries &&
        duration &&
        duration < carouselInterval
            ? duration
            : carouselInterval;

    const moveToNextItem = useCallback(() => {
        setCarouselState(({ index }) => ({
            index: (index + 1) % mediaCount,
            time: 0,
        }));
    }, [setCarouselState, mediaCount]);

    // Update the current carousel animation time, and move to the next item if the time is over
    const updateCurrentTime = useCallback(
        /*
         * timeUpdateAction is a state reducer for updating carouselState.time:
         * either a new time value or a callback to update it by the previous value.
         * See the docs of useState and SetStateAction.
         */
        (timeUpdateAction: SetStateAction<number>) => {
            setCarouselState(({ index, time }) => {
                const newCurrentTime =
                    typeof timeUpdateAction === "function"
                        ? timeUpdateAction(time)
                        : timeUpdateAction;

                if (newCurrentTime < carouselInterval || mediaCount === 1) {
                    // Current video time is within the carousel interval (or not relevant
                    //  because there's no carousel) - just update the time state.
                    return {
                        index,
                        time: newCurrentTime,
                    };
                } else {
                    // Current video time exceeded the carousel interval - move to the next entry.
                    return {
                        index: (index + 1) % mediaCount,
                        time: 0,
                    };
                }
            });
        },
        [setCarouselState, mediaCount, carouselInterval]
    );

    // "now" variable will be updated with the current time each animation frame
    useCurrentTimeForAnimation(
        (delta) => {
            if (isActive) {
                updateCurrentTime((currentTime) => currentTime + delta / 1000);
            }
        },
        500,
        [isActive, updateCurrentTime]
    );

    const [player, setPlayer] = useState(null);

    const onPlayerReadyHandler = useCallback(
        // ignore, we do not know the player type!!
        // eslint-disable-next-line
        (playerInstance: any) => {
            setPlayer(playerInstance);

            // Subscribe to the player stop event to know when the entry finishes before reaching the carousel interval
            playerInstance.addEventListener(
                playerInstance.Event.Core.ENDED,
                moveToNextItem
            );
        },
        [moveToNextItem]
    );

    const toggleIsActiveClickHandler = useCallback(
        (ev: MouseEvent<HTMLButtonElement>) => {
            ev.preventDefault();
            ev.stopPropagation();

            setIsActive((isActive) => !isActive);
        },
        []
    );

    const playerMedia = useMemo(() => ({ entryId: id }), [id]);

    const customizedPlayerConfig = useMemo(() => {
        if (mediaCount === 1) {
            // The video playback should be looped if there's only 1 video in the carousel
            return merge({}, previewPlayerConfig, {
                playback: {
                    loop: true,
                },
            });
        }

        return previewPlayerConfig;
    }, [previewPlayerConfig, mediaCount]);

    const onLineClick = ({ index }) => {
        setCarouselState({
            index,
            time: 0,
        });
        if (currentItemIndex === index) {
            // The clicked item is already current - reset the state
            if (player) {
                if (player.isLive()) {
                    // Resume the live session for the live entries
                    player.seekToLiveEdge();
                } else {
                    // Start from the beginning for the VOD entries
                    player.currentTime = 0;
                }
            }
        }
        if (!isActive) {
            setIsActive(true);
        }
    };

    const config = useConfig();
    const enableEntryTitles = config?.application?.enableEntryTitles;
    const entryName = enableEntryTitles ? name : undefined;

    if (!enabled) {
        return null;
    }

    return (
        <HeroUnit
            entryCarouselInterval={entryCarouselInterval}
            className={className}
            id={id}
            entryName={entryName}
            name={name}
            isLive={isLive}
            currentTime={currentTime}
            title={title}
            presenters={presenters}
            description={description}
            onLineClick={onLineClick}
            currentItemIndex={currentItemIndex}
            mediaCount={mediaCount}
            isActive={isActive}
            playerMedia={playerMedia}
            customizedPlayerConfig={customizedPlayerConfig}
            onPlayerReadyHandler={onPlayerReadyHandler}
            toggleIsActiveClickHandler={toggleIsActiveClickHandler}
            thumbnailUrl={thumbnailUrl}
            staticImage={
                mediaType === MediaType.StaticImages
                    ? staticImages[carouselState.index]
                    : undefined
            }
            itemsNames={
                mediaType === MediaType.StaticImages
                    ? staticImages.map((image) => image.title || image.alt)
                    : entries.map((entry) => entry.name)
            }
        />
    );
};

export default HeroUnitContainer;
